import { validate as isValidUUID } from 'uuid';
import {
  STOREFRONT_PATH_PREFIX,
  StorefrontBootIntent,
} from './boot-params-types';

/**
 * Storefront URL boot params — web implementation.
 *
 * The boot URL is read exactly ONCE, at module evaluation, into the module
 * constants below — it is never re-read, and react-navigation never gets a
 * `linking` prop. A partial linking config is deliberately avoided: merely
 * mounting the root container would rewrite the live URL and strip its query
 * params, breaking sales-portal deep links, punchout, and smart-order (all of
 * which read those params off the live URL). Instead we parse the URL once
 * here and drive navigation imperatively.
 *
 * SAFETY: the module-scope `window` access below is the single allowed one.
 * It is safe ONLY because this is a `.web.ts` platform split that is never
 * evaluated on native — module-scope `window` access in shared code has
 * crashed native startup before (PR #4947). Do not copy this pattern into
 * shared modules.
 */

// The Next.js shell only requires the app behind a
// `typeof window !== 'undefined'` check (pages/index.page.tsx), so window
// always exists when this module evaluates in a browser; the guard keeps
// any future server-side evaluation inert instead of crashing the build.
const bootPathname =
  typeof window === 'undefined' ? '' : window.location.pathname;
const bootSearch = typeof window === 'undefined' ? '' : window.location.search;

/**
 * Query params owned by flows that read the LIVE URL long after boot:
 * punchout reads `punchOutCookie`/`userID` at checkout time
 * (new-order-actions-context) and smart-order reads
 * `sessionKey`/`smartOrderID` minutes into the session (order-summary,
 * order-drawer-manager). A single URL write breaks those sessions, so if
 * ANY of these keys was present at boot, every storefront URL write is
 * disabled for the entire session. Any new query-param-driven flow must add
 * its key to this list — the source hooks (use-punchout-params.web,
 * use-smart-order-params.web) carry a matching breadcrumb. Exported for
 * the boot-params tests, so the suite exercises the real list rather than
 * a copy that can drift.
 */
export const RESERVED_BOOT_QUERY_KEYS = [
  'punchOutCookie',
  'sessionKey',
  'smartOrderID',
  'userID',
];

// Storefront product ids are slugs; anything else is not a storefront URL.
const PRODUCT_ID_PATTERN = /^[a-z0-9-]+$/;

const bootSearchParams = new URLSearchParams(bootSearch);

const parseBootStorefrontIntent = (): StorefrontBootIntent | null => {
  const segments = bootPathname
    .split('/')
    .filter((segment) => segment.length > 0);
  if (segments.length !== 2 || `/${segments[0]}` !== STOREFRONT_PATH_PREFIX) {
    return null;
  }

  const productId = segments[1];
  if (!PRODUCT_ID_PATTERN.test(productId)) {
    return null;
  }

  const restaurantUUID = bootSearchParams.get('restaurantUUID');
  // A malformed restaurantUUID drops the param, not the intent: the user
  // still lands on the product under their currently selected restaurant.
  // uuid.validate is RFC-strict (version + variant nibbles), which is
  // strictly safer than a loose hex regex for an untrusted URL param.
  return restaurantUUID !== null && isValidUUID(restaurantUUID)
    ? // Normalized: relationship-map keys are lowercase UUIDs.
      { productId, restaurantUUID: restaurantUUID.toLowerCase() }
    : { productId };
};

/**
 * The storefront deep-link intent parsed from the boot URL, or null when
 * the app did not boot on `/storefront/:productId`.
 */
export const bootStorefrontIntent: StorefrontBootIntent | null =
  parseBootStorefrontIntent();

/**
 * True if any reserved query param was present at boot. Every storefront URL
 * write for the entire session is then suppressed — enforced below in
 * `applyStorefrontProductUrl` / `clearStorefrontProductUrl`, the only URL
 * writers this feature has.
 */
export const urlWritesDisabled: boolean = RESERVED_BOOT_QUERY_KEYS.some((key) =>
  bootSearchParams.has(key),
);

let bootIntentConsumed = false;

/**
 * One-shot accessor for the boot intent: returns it on the first call and
 * null forever after. The one-shot is essential — a seed that can be read
 * more than once gets re-applied after the restaurant-key remount and
 * reverts the user's manual restaurant switches (the same bug class
 * previously fixed for notification routing via the
 * `lastProcessedNotificationID` latch in app-stack).
 */
export const consumeBootStorefrontIntent = (): StorefrontBootIntent | null => {
  if (bootIntentConsumed) {
    return null;
  }
  bootIntentConsumed = true;
  return bootStorefrontIntent;
};

/**
 * True while a boot intent exists and has not been consumed yet. Used by
 * app-container to defeat a rehydrated SALES_PORTAL experience mode exactly
 * until the intent is applied — afterwards manual experience switches win
 * again.
 */
export const isBootStorefrontIntentPending = (): boolean =>
  bootStorefrontIntent !== null && !bootIntentConsumed;

/**
 * Sets the URL to `/storefront/:productId`. Always `replaceState`, never
 * `pushState` (no new history entries: browser back keeps today's behavior
 * of leaving the site, and no id-less entries desync react-navigation),
 * always preserving the existing `history.state`. No-op for the whole
 * session when reserved-param writes are disabled (see urlWritesDisabled).
 */
export const applyStorefrontProductUrl = (productId: string): void => {
  if (urlWritesDisabled) {
    return;
  }
  // productId arrives via route params — never write an unvalidated
  // value into the address bar.
  if (!PRODUCT_ID_PATTERN.test(productId)) {
    return;
  }
  window.history.replaceState(
    window.history.state,
    '',
    `${STOREFRONT_PATH_PREFIX}/${productId}`,
  );
};

/**
 * Restores the operator-portal home URL (`/`) — same constraints as
 * `applyStorefrontProductUrl`: replaceState only, history.state preserved,
 * no-op when reserved-param writes are disabled (see urlWritesDisabled).
 */
export const clearStorefrontProductUrl = (): void => {
  if (urlWritesDisabled) {
    return;
  }
  window.history.replaceState(window.history.state, '', '/');
};
